// The one place a calendar day is turned into text for the UI.
//
// LOCALE POLICY — decided here, deliberately: a calendar day is ALWAYS written `dd.MM.yyyy`,
// zero-padded, in EVERY locale the SPA ships (fi and en). An English-locale reader does NOT get
// their own convention. Three reasons, in order of weight:
//
//   1. These dates are compliance evidence — certificate issue/validity, training due dates,
//      coverage rows. `05.03.2026` read as en-US (M/D/YYYY) and as en-GB (D/M/YYYY) are two
//      DIFFERENT days, and nothing in a printed report tells the reader which one was meant.
//      One written form removes the ambiguity instead of localising it.
//   2. Padding is not cosmetic: `1.9.2026` and `12.11.2026` do not align in a table column, and
//      the admin tables render these in columns. Intl's `dateStyle` gives unpadded `d.M.yyyy`
//      for fi, so Intl cannot deliver this even for the primary locale.
//   3. The product is Finnish-market; fi is the primary locale and en is a second reading of the
//      SAME records, not a separate market with its own conventions.
//
// Consequence, stated so it is not rediscovered as a bug: this module uses no `Intl` and no
// `toLocaleDateString`, and takes no locale argument. A date and time TOGETHER (an audit event,
// a questionnaire window) is a different thing and stays on `Intl.DateTimeFormat` at its call
// site — the policy above is about a calendar DAY.
//
// Timezones: a `yyyy-MM-dd` value is a calendar day with no timezone, so it is read
// LEXICALLY — never through `new Date("2026-09-12")`, which is UTC midnight and renders as the
// previous day for anyone west of Greenwich. A `Date` is read through its LOCAL calendar fields,
// because a `Date` in this app comes from an instant the caller already decided to show in local
// time.
//
// Tested by `date.test.mjs` (Layer 2, ADR-0002 entry rule A — pure decision logic).

/** What an absent date renders as. The same glyph the tables already use for an empty cell. */
export const NO_DATE = "—";

/** `yyyy-MM-dd`, optionally followed by a time part we ignore (an ISO instant's date portion). */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/;

/**
 * Format a calendar day as zero-padded `dd.MM.yyyy`, in every locale.
 *
 * Accepts a date-only `yyyy-MM-dd` string (read lexically), a longer ISO string (its date part is
 * taken as written — the caller owns the timezone question), or a `Date` (its local fields).
 * A value that is not a real calendar day renders as {@link NO_DATE} rather than `NaN.NaN.NaN`.
 */
export function formatDate(value: string | Date): string {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return NO_DATE;
    return write(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }

  const m = ISO_DATE.exec(value.trim());
  if (!m) return NO_DATE;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!isRealDay(year, month, day)) return NO_DATE;
  return write(year, month, day);
}

/**
 * The nullable case: `formatDate`, with an absent value rendered as {@link NO_DATE}.
 *
 * This exists so a caller never spells the placeholder itself — an empty cell written `-` on one
 * screen and `—` on another is the same inconsistency this module was written to end.
 */
export function formatDateOrDash(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === "") return NO_DATE;
  return formatDate(value);
}

/** The padding rule, in one place. */
function write(year: number, month: number, day: number): string {
  return `${pad2(day)}.${pad2(month)}.${String(year).padStart(4, "0")}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Rejects a well-shaped string that is not a day on the calendar (`2026-02-30`, `2026-13-01`). */
function isRealDay(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
}
